import { IPage, IPageRange } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import { Reducer } from 'redux';
import ContentConstants from '../contentConstants';
import {
	DistributionContactsListSourceType,
	DistributionType,
	IColumn,
	IDistributionContact,
	SurveyParticipantsMenuActions,
} from '../models';
import { SurveyCompletionStatusType } from '../models/SurveyCompletionStatusType';
import * as DistributionContactAPI from '../services/DistributionContactAPI';
import * as SurveyParticipantAPI from '../services/SurveyParticipantAPI';
import { ColumnHelper } from '../utilities';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { IApplicationState } from './';
import {
	ISurveyParticipantReopenAndResendCheckboxToggle,
	ISurveyParticipantReopenFailed,
	ISurveyParticipantReopenRequested,
	ISurveyParticipantReopenSuccess,
	ISurveyParticipantReopenWarningModalToggle,
	ISurveyParticipantResendEmailFailed,
	ISurveyParticipantResendEmailRequested,
	ISurveyParticipantResendEmailSuccess,
	ISurveyParticipantResendEmailWarningModalToggle,
	ISurveyParticipantResetAndResendCheckboxToggle,
	ISurveyParticipantResetFailed,
	ISurveyParticipantResetRequested,
	ISurveyParticipantResetSuccess,
	ISurveyParticipantResetWarningModalToggle,
} from './Actions';
import { ActionTypes } from './ActionTypes';
import { actionsColumn, DistributionColumns, surveyParticipantDefaultColumns, surveyStatusColumn } from './DistributionContactStore';

export const SurveyAdminMenuItems = [
	{ name: SurveyParticipantsMenuActions.Reset, displayText: 'Reset Survey', disabled: false, displayOrder: 1 },
	{ name: SurveyParticipantsMenuActions.Reopen, displayText: 'Reopen Completed Survey', disabled: false, displayOrder: 2 },
	{ name: SurveyParticipantsMenuActions.Resend, displayText: 'Resend Email', disabled: false, displayOrder: 3 },
	{ name: SurveyParticipantsMenuActions.Delete, displayText: 'Delete', disabled: false, displayOrder: 4 },
	{ name: SurveyParticipantsMenuActions.Export, displayText: 'Export Distribution', disabled: false, displayOrder: 5 },
];

export interface ISurveyParticipantState {
	isReopening: boolean;
	isReopenAndResend?: boolean;
	isReopenWarningModalOpen: boolean;
	isResendingEmail?: boolean;
	isResendWarningModalOpen: boolean;
	isResetting: boolean;
	isResetWarningModalOpen: boolean;
	isResetAndResend?: boolean;
}

export const defaultState: ISurveyParticipantState = {
	isReopening: false,
	isReopenAndResend: false,
	isReopenWarningModalOpen: false,
	isResendingEmail: false,
	isResendWarningModalOpen: false,
	isResetting: false,
	isResetWarningModalOpen: false,
	isResetAndResend: false,
};

export function GetSurveyParticipantActionMenuItems(
	distributionContactsListSourceType: DistributionContactsListSourceType,
	distributionType: DistributionType,
	selectedDistributionContacts: IDistributionContact[],
) {
	let filteredMenuItems = SurveyAdminMenuItems;
	if (
		distributionType !== DistributionType.SingleLinkWithoutDistribution &&
		distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen
	) {
		if (distributionType === DistributionType.SingleLink) {
			// Do not show Reopen and Reset menu for SingleLink distribution
			filteredMenuItems = _.filter(filteredMenuItems, (item) => {
				return item.name !== SurveyParticipantsMenuActions.Reopen && item.name !== SurveyParticipantsMenuActions.Reset;
			});
		} else if (DistributionHelper.isNotAnonymous(distributionType)) {
			// Disable Reopen menu if there any survey status is not "Complete"
			const reopenMenuItem = _.find(filteredMenuItems, (item) => item.name === SurveyParticipantsMenuActions.Reopen);
			if (reopenMenuItem) {
				reopenMenuItem.disabled =
					_.filter(selectedDistributionContacts, (contact) => {
						return contact.surveyCompletionStatusId !== SurveyCompletionStatusType.Complete;
					}).length !== 0;
			}
			// Disable Reset menu if there any survey with status not in "Complete" and "InProgress"
			const resetMenuItem = _.find(filteredMenuItems, (item) => item.name === SurveyParticipantsMenuActions.Reset);
			if (resetMenuItem) {
				resetMenuItem.disabled =
					_.filter(selectedDistributionContacts, (contact) => {
						return (
							contact.surveyCompletionStatusId !== SurveyCompletionStatusType.Complete &&
							contact.surveyCompletionStatusId !== SurveyCompletionStatusType.InProgress
						);
					}).length !== 0;
			}
		}
		filteredMenuItems = _.filter(filteredMenuItems, (item) => {
			return item.name !== SurveyParticipantsMenuActions.Delete && item.name !== SurveyParticipantsMenuActions.Export;
		});
	} else {
		filteredMenuItems = _.filter(filteredMenuItems, (item) => {
			return item.name === SurveyParticipantsMenuActions.Delete || item.name === SurveyParticipantsMenuActions.Export;
		});
	}
	return filteredMenuItems;
}

export const ActionCreators = {
	GetPagedSurveyParticipants:
		(
			surveyId: number,
			audienceId: number,
			pageRange: IPageRange,
			searchText: string,
			sortedBy: string,
			sortAsc: boolean,
			selectedColumns?: IColumn[],
			selectedDistributionContactIds?: number[],
			showDuplicateEmailAddresses?: boolean,
		) =>
		(dispatch: any) => {
			const columns = _.cloneDeep(selectedColumns);
			if (_.every(columns, (c) => c.name !== DistributionColumns.surveyCompletionStatus)) {
				columns?.push({ ...surveyStatusColumn });
			}
			dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_GET_REQUESTED });
			const extraColumns = ColumnHelper.GetExtraColumnNames([...surveyParticipantDefaultColumns, actionsColumn], columns);

			return SurveyParticipantAPI.getSurveyParticipants(
				surveyId,
				audienceId,
				pageRange.offset,
				pageRange.limit,
				searchText,
				sortedBy,
				sortAsc,
				extraColumns,
				selectedDistributionContactIds,
				showDuplicateEmailAddresses,
			)
				.then((pagedDistributionContacts: IPage<IDistributionContact>) => {
					dispatch({
						type: ActionTypes.DISTRIBUTION_CONTACTS_GET_SUCCEEDED,
						payload: { pagedDistributionContacts, searchText, sortedBy, sortAsc, columns: columns },
					});
				})
				.catch(() => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_GET_FAILED });
				});
		},
	GetSurveyParticipantsCount:
		(surveyId: number, audienceId: number, searchText: string, getOnlyDuplicateEmails?: boolean) => (dispatch: any) => {
			dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_REQUESTED });

			return SurveyParticipantAPI.getSurveyParticipantsCount(surveyId, audienceId, searchText, getOnlyDuplicateEmails)
				.then((count: number) => {
					dispatch({
						type: ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_SUCCEEDED,
						payload: count,
					});
				})
				.catch(() => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_FAILED });
				});
		},
	DeleteSurveyParticipants:
		(surveyId: number, audienceId: number, distributionContactIds: number[]) => (dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_REQUESTED });

			return DistributionContactAPI.deleteDistributionContacts(surveyId, audienceId, distributionContactIds)
				.then(() => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_SUCCEEDED });
					dispatch({
						type: ActionTypes.TOAST_INFO_NOTIFICATION,
						payload: ContentConstants.TOAST_DISTRIBUTION_CONTACT_DELETED.replace('{total}', distributionContactIds.length.toString()),
					});

					const { pageRange, searchText, sortParams, columns } = getState().distributionContacts;

					// get participants
					dispatch(
						ActionCreators.GetPagedSurveyParticipants(
							surveyId,
							audienceId,
							pageRange,
							searchText,
							sortParams.sortedBy!,
							sortParams.sortAsc!,
							columns,
							[],
						),
					);
				})
				.catch(() => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_FAILED });

					dispatch({
						type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
						payload: {
							messageTitle: ContentConstants.TOAST_DELETE_FAILED_TITLE,
							message: ContentConstants.TOAST_SURVEY_PARTICIPANT_ACTION_FAILURE_BODY,
						},
					});
				});
		},
	Resend: (surveyId: number, audienceId: number, distributionContactIds: number[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_REQUESTED });

		return SurveyParticipantAPI.Resend(surveyId, audienceId, distributionContactIds)
			.then(() => {
				dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_SUCCEEDED });

				dispatch({
					type: ActionTypes.TOAST_SUCCESS_NOTIFICATION,
					payload: ContentConstants.TOAST_RESEND_EMAIL_SUCCESS.replace('{total}', distributionContactIds.length.toString()),
				});
			})
			.catch(() => {
				dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_FAILED });

				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						messageTitle: ContentConstants.TOAST_RESEND_FAILED_TITLE,
						message: ContentConstants.TOAST_SURVEY_PARTICIPANT_ACTION_FAILURE_BODY,
					},
				});
			});
	},
	Reopen:
		(surveyId: number, audienceId: number, distributionContactIds: number[], resend = false) =>
		(dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_REOPEN_REQUESTED });

			return SurveyParticipantAPI.Reopen(surveyId, audienceId, distributionContactIds, resend)
				.then(() => {
					dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_REOPEN_SUCCEEDED });
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_UPDATE_STATUS_AFTER_REOPEN });

					const message = resend
						? ContentConstants.TOAST_REOPEN_RESEND_SUCCESS.replace('{total}', distributionContactIds.length.toString())
						: ContentConstants.TOAST_REOPEN_SUCCESS.replace('{total}', distributionContactIds.length.toString());
					dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: message });

					const { pageRange, searchText, sortParams, columns, selectedDistributionContacts } = getState().distributionContacts;
					const selectedDistributionContactIds = selectedDistributionContacts
						? selectedDistributionContacts.map((contact) => {
								return contact.distributionContactId;
						  })
						: [];

					// get participants
					dispatch(
						ActionCreators.GetPagedSurveyParticipants(
							surveyId,
							audienceId,
							pageRange,
							searchText,
							sortParams.sortedBy!,
							sortParams.sortAsc!,
							columns,
							selectedDistributionContactIds,
						),
					);
				})
				.catch(() => {
					dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_REOPEN_FAILED });

					dispatch({
						type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
						payload: {
							messageTitle: ContentConstants.TOAST_REOPEN_FAILED_TITLE,
							message: ContentConstants.TOAST_SURVEY_PARTICIPANT_ACTION_FAILURE_BODY,
						},
					});
				});
		},

	Reset:
		(surveyId: number, audienceId: number, distributionContactIds: number[], resend = false) =>
		(dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESET_REQUESTED });

			return SurveyParticipantAPI.Reset(surveyId, audienceId, distributionContactIds, resend)
				.then(() => {
					dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESET_SUCCEEDED });
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_UPDATE_STATUS_AFTER_RESET });

					const message = resend
						? ContentConstants.TOAST_RESET_RESEND_SUCCESS.replace('{total}', distributionContactIds.length.toString())
						: ContentConstants.TOAST_RESET_SUCCESS.replace('{total}', distributionContactIds.length.toString());
					dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: message });

					const { pageRange, searchText, sortParams, columns, selectedDistributionContacts } = getState().distributionContacts;
					const selectedDistributionContactIds = selectedDistributionContacts
						? selectedDistributionContacts.map((contact) => {
								return contact.distributionContactId;
						  })
						: [];

					// get participants
					dispatch(
						ActionCreators.GetPagedSurveyParticipants(
							surveyId,
							audienceId,
							pageRange,
							searchText,
							sortParams.sortedBy!,
							sortParams.sortAsc!,
							columns,
							selectedDistributionContactIds,
						),
					);
				})
				.catch(() => {
					dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESET_FAILED });

					dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				});
		},
	ToggleResendWarningModal: (isOpen: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_WARNING_MODAL_TOGGLE, payload: isOpen });
	},

	ToggleReopenWarningModal: (isOpen: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_REOPEN_WARNING_MODAL_TOGGLE, payload: isOpen });
	},
	ToggleResetWarningModal: (isOpen: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESET_WARNING_MODAL_TOGGLE, payload: isOpen });
	},
	ToggleReopenResendCheckbox: (checked: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_REOPEN_RESEND_CHECKBOX_TOGGLE, payload: checked });
	},
	ToggleResetResendCheckbox: (checked: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PARTICIPANT_RESET_RESEND_CHECKBOX_TOGGLE, payload: checked });
	},
};

type KnownActions =
	| ISurveyParticipantResendEmailRequested
	| ISurveyParticipantResendEmailSuccess
	| ISurveyParticipantResendEmailFailed
	| ISurveyParticipantResendEmailWarningModalToggle
	| ISurveyParticipantReopenRequested
	| ISurveyParticipantReopenSuccess
	| ISurveyParticipantReopenFailed
	| ISurveyParticipantReopenWarningModalToggle
	| ISurveyParticipantReopenAndResendCheckboxToggle
	| ISurveyParticipantResetRequested
	| ISurveyParticipantResetSuccess
	| ISurveyParticipantResetFailed
	| ISurveyParticipantResetWarningModalToggle
	| ISurveyParticipantResetAndResendCheckboxToggle;

export const reducer: Reducer<ISurveyParticipantState, KnownActions> = (
	state: ISurveyParticipantState = defaultState,
	action: KnownActions,
) => {
	switch (action.type) {
		case ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_REQUESTED:
			return { ...state, isResendingEmail: true };
		case ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_SUCCEEDED:
			return { ...state, isResendingEmail: false, isResendWarningModalOpen: false };
		case ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_FAILED:
			return { ...state, isResendingEmail: false };
		case ActionTypes.SURVEY_PARTICIPANT_RESEND_EMAIL_WARNING_MODAL_TOGGLE: {
			return { ...state, isResendWarningModalOpen: action.payload };
		}
		case ActionTypes.SURVEY_PARTICIPANT_REOPEN_WARNING_MODAL_TOGGLE: {
			return { ...state, isReopenWarningModalOpen: action.payload, isReopenAndResend: false };
		}
		case ActionTypes.SURVEY_PARTICIPANT_REOPEN_RESEND_CHECKBOX_TOGGLE: {
			return { ...state, isReopenAndResend: action.payload };
		}
		case ActionTypes.SURVEY_PARTICIPANT_REOPEN_REQUESTED:
			return { ...state, isReopening: true };
		case ActionTypes.SURVEY_PARTICIPANT_REOPEN_SUCCEEDED:
			return { ...state, isReopening: false, isReopenWarningModalOpen: false };
		case ActionTypes.SURVEY_PARTICIPANT_REOPEN_FAILED:
			return { ...state, isReopening: false };
		case ActionTypes.SURVEY_PARTICIPANT_RESET_WARNING_MODAL_TOGGLE: {
			return { ...state, isResetWarningModalOpen: action.payload, isResetAndResend: false };
		}
		case ActionTypes.SURVEY_PARTICIPANT_RESET_RESEND_CHECKBOX_TOGGLE: {
			return { ...state, isResetAndResend: action.payload };
		}
		case ActionTypes.SURVEY_PARTICIPANT_RESET_REQUESTED:
			return { ...state, isResetting: true };
		case ActionTypes.SURVEY_PARTICIPANT_RESET_SUCCEEDED:
			return { ...state, isResetting: false, isResetWarningModalOpen: false };
		case ActionTypes.SURVEY_PARTICIPANT_RESET_FAILED:
			return { ...state, isResetting: false };
		default:
			return state;
	}
};
